import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Typography,
} from '@material-ui/core';
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';
import PhoneIcon from '@material-ui/icons/Phone';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import Whatsapp from '../../components/Icons/Whatsapp';
import EditClientDataModal from '../../components/ClientInfoPage/EditClientDataModal/EditClientDataModal';
import { DOCUMENTS_TYPES } from '../../constants.js/generals';
import { errorToast, formatDate, formatNumber, routes, successToast } from '../../constants.js/misc';
import { getClientService } from '../../services/clients';
import useStyles from './ClientInfoPageMobile.styles';

const toTelHref = (phone) => {
    const raw = String(phone || '').trim().replace(/\s/g, '');
    if (!raw) {
        return '';
    }
    const digits = raw.replace(/[^\d+]/g, '');
    if (!digits) {
        return '';
    }
    return digits.startsWith('+') ? `tel:${digits}` : `tel:+${digits.replace(/\D/g, '')}`;
};

const totalWithShipping = (orderShippingCost, storeData) => {
    if (orderShippingCost) {
        return isNaN(orderShippingCost)
            ? (isNaN(storeData?.delivery_cost_details) || !storeData?.delivery_cost_enabled
                ? 0
                : parseInt(storeData.delivery_cost_details, 10))
            : orderShippingCost;
    }
    return isNaN(storeData?.delivery_cost_details) || !storeData?.delivery_cost_enabled
        ? 0
        : parseInt(storeData.delivery_cost_details, 10);
};

const orderLineTotal = (order, storeData) => {
    const sub = (order?.order_details || []).reduce(
        (acc, detail) => acc + (detail.price * detail.quantity),
        0,
    );
    return sub + totalWithShipping(order?.shipping_cost, storeData);
};

export const ClientInfoPageMobile = ({ storeId, clientToEdit, storeData }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { id } = useParams();

    const [gettingClient, setGettingClient] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [ordersVisible, setOrdersVisible] = useState(6);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [notesModalOpen, setNotesModalOpen] = useState(false);

    useEffect(() => {
        if (!id) {
            return;
        }
        if (clientToEdit) {
            setClientData(clientToEdit);
        }
        setGettingClient(true);
        getClientService(storeId, id)
            .then((res) => {
                setGettingClient(false);
                setClientData(res.data);
            })
            .catch(() => {
                setGettingClient(false);
                enqueueSnackbar(t('OrderDetail.dataError'), errorToast);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const message = useMemo(
        () =>
            `Hola, ${clientData?.first_name} ${clientData?.last_name},
Te contactamos de ${storeData?.name}
`,
        [clientData?.first_name, clientData?.last_name, storeData?.name],
    );

    const handleClickWhatsApp = useCallback(() => {
        if (!clientData?.phone_number) {
            return;
        }
        const messageUrl = `https://wa.me/${String(clientData.phone_number).replace(/\D/g, '')}?text=`;
        window.open(`${messageUrl}${encodeURIComponent(message).replace(/%20/g, '+')}`);
    }, [clientData?.phone_number, message]);

    const handleCopy = useCallback(
        async (text) => {
            if (!text) {
                return;
            }
            try {
                await navigator.clipboard.writeText(text);
                enqueueSnackbar(t('Config.copyIP'), successToast);
            } catch {
                enqueueSnackbar(t('OrderDetail.dataError'), errorToast);
            }
        },
        [enqueueSnackbar, t],
    );

    const fullName = clientData
        ? `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim()
        : '';
    const documentTypeLabel = DOCUMENTS_TYPES[clientData?.document_type]?.name || '';
    const addressLine = useMemo(() => {
        if (!clientData) {
            return '';
        }
        const parts = [
            clientData.address,
            clientData.address_line,
            clientData.city?.name,
            clientData.city?.state,
            clientData.city?.country_code,
        ].filter(Boolean);
        return parts.join(', ');
    }, [clientData]);

    const sortedOrders = useMemo(() => {
        const list = [...(clientData?.orders || [])];
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return list;
    }, [clientData?.orders]);

    const visibleOrders = useMemo(
        () => sortedOrders.slice(0, ordersVisible),
        [sortedOrders, ordersVisible],
    );

    const lastOrderDate = useMemo(() => {
        if (!sortedOrders.length) {
            return null;
        }
        return sortedOrders[0]?.created_at;
    }, [sortedOrders]);

    const statusChipClass = (statusName) => {
        const s = String(statusName || '').toLowerCase();
        if (s.includes('cancel') || s.includes('fall')) {
            return `${classes.chip} ${classes.chipError}`;
        }
        if (s.includes('pagad') || s.includes('entreg')) {
            return `${classes.chip} ${classes.chipSuccess}`;
        }
        if (s.includes('proces') || s.includes('pend') || s.includes('espera')) {
            return `${classes.chip} ${classes.chipWarning}`;
        }
        return `${classes.chip} ${classes.chipNeutral}`;
    };

    const quantity = Number(clientData?.quantity) || 0;
    const total = Number(clientData?.total) || 0;
    const avg = quantity > 0 ? total / quantity : 0;
    const currency = storeData?.currency;

    const handleProfileSaved = () => {
        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
        setTimeout(() => window.location.reload(), 800);
        setProfileModalOpen(false);
    };

    const handleNotesSaved = () => {
        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
        setTimeout(() => window.location.reload(), 800);
        setNotesModalOpen(false);
    };

    return (
        <Box component="main" className={classes.main}>
            <Box className={classes.section}>
                <Box className={classes.topBar}>
                    <IconButton
                        component={RouterLink}
                        to={routes.clientPage}
                        className={classes.topBarBack}
                        aria-label={t('goBack')}
                    >
                        <ArrowBackIcon style={{ fontSize: 22 }} />
                    </IconButton>
                    <Typography component="h1" className={classes.topBarTitle}>
                        {t('ClientDetail.clientModal.ClientTitle')}
                    </Typography>
                    <Box className={classes.topBarSpacer} aria-hidden />
                </Box>
                {gettingClient && <LinearProgress />}
            </Box>

            {!gettingClient && clientData && (
                <>
                    <Box component="section" id="customer_header" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h2" className={classes.customerTitle}>
                                {fullName || t('ClientDetail.clientModal.ClientTitle')}
                            </Typography>
                            <Box className={classes.actionRow}>
                                <Button
                                    component={RouterLink}
                                    to={routes.createOrder}
                                    className={classes.ctaButton}
                                    startIcon={<AddOutlinedIcon style={{ fontSize: 18 }} />}
                                >
                                    {t('ClientDetail.mobile.createOrder')}
                                </Button>
                                <IconButton
                                    type="button"
                                    className={classes.iconSquareButton}
                                    aria-label={t('ClientDetail.contactBtn')}
                                    onClick={handleClickWhatsApp}
                                    disabled={!clientData.phone_number}
                                >
                                    <Whatsapp style={{ fontSize: 20, color: '#16a34a' }} />
                                </IconButton>
                                <IconButton
                                    component="a"
                                    href={toTelHref(clientData.phone_number)}
                                    className={classes.iconSquareButton}
                                    aria-label={t('ClientDetail.mobile.call')}
                                    disabled={!toTelHref(clientData.phone_number)}
                                >
                                    <PhoneIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    <Box component="section" id="stats" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h3" className={classes.sectionTitle}>
                                {t('ClientDetail.mobile.statsTitle')}
                            </Typography>
                            <Box className={classes.statsGrid}>
                                <Box className={classes.statCell}>
                                    <Typography className={classes.statValue}>
                                        {formatNumber(total, currency)}
                                    </Typography>
                                    <Typography className={classes.statLabel}>
                                        {t('ClientDetail.mobile.statSpend')}
                                    </Typography>
                                </Box>
                                <Box className={classes.statCellMiddle}>
                                    <Typography className={classes.statValue}>{quantity}</Typography>
                                    <Typography className={classes.statLabel}>
                                        {t('ClientDetail.ordersQuantity')}
                                    </Typography>
                                </Box>
                                <Box className={classes.statCell}>
                                    <Typography className={classes.statValue}>
                                        {formatNumber(avg, currency)}
                                    </Typography>
                                    <Typography className={classes.statLabel}>
                                        {t('ClientDetail.mobile.statAverage')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box component="section" id="orders" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h3" className={classes.sectionTitle}>
                                {t('ClientOrdersTable.detailsTitle')}
                            </Typography>
                            <Box className={classes.ordersScroll}>
                                {visibleOrders.length === 0 && (
                                    <Typography className={classes.emptyOrders}>
                                        {t('ClientDetail.mobile.noOrders')}
                                    </Typography>
                                )}
                                {visibleOrders.map((order) => (
                                    <Box key={order.id} className={classes.orderBlock}>
                                        <Box className={classes.orderRow}>
                                            <RouterLink
                                                to={`${routes.ordersPage}/${order.id}`}
                                                className={classes.orderLink}
                                            >
                                                #{order.sequence}
                                            </RouterLink>
                                            <span className={statusChipClass(order?.order_status?.name)}>
                                                {order?.order_status?.name || '—'}
                                            </span>
                                        </Box>
                                        <Box className={classes.orderRow}>
                                            <Typography className={classes.orderAmount}>
                                                {formatNumber(orderLineTotal(order, storeData), currency)}
                                            </Typography>
                                            <Typography className={classes.orderDate}>
                                                {formatDate(order.created_at)}
                                            </Typography>
                                        </Box>
                                        <Button
                                            component={RouterLink}
                                            to={`${routes.ordersPage}/${order.id}`}
                                            className={classes.outlineButton}
                                            variant="outlined"
                                        >
                                            {t('ClientDetail.mobile.viewOrder')}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                            {sortedOrders.length > ordersVisible && (
                                <Button
                                    type="button"
                                    className={classes.loadMoreButton}
                                    onClick={() => setOrdersVisible((n) => n + 6)}
                                >
                                    {t('ClientDetail.mobile.loadMoreOrders')}
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Box component="section" id="info" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h3" className={classes.sectionTitle}>
                                {t('ClientDetail.mobile.clientInfoTitle')}
                            </Typography>

                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.labelName')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>{fullName}</Typography>
                                </Box>
                                <IconButton
                                    type="button"
                                    className={classes.iconRoundButton}
                                    aria-label={t('ClientDetail.details.edit')}
                                    onClick={() => setProfileModalOpen(true)}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.labelWhatsapp')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {clientData.phone_number
                                            ? `+${String(clientData.phone_number).replace(/^\+/, '')}`
                                            : '—'}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        type="button"
                                        className={classes.iconRoundButton}
                                        aria-label={t('PreviewLink.copy')}
                                        onClick={() =>
                                            handleCopy(
                                                clientData.phone_number
                                                    ? `+${String(clientData.phone_number).replace(/^\+/, '')}`
                                                    : '',
                                            )
                                        }
                                        disabled={!clientData.phone_number}
                                    >
                                        <FileCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        type="button"
                                        className={classes.iconRoundButton}
                                        aria-label={t('ClientDetail.contactBtn')}
                                        onClick={handleClickWhatsApp}
                                        disabled={!clientData.phone_number}
                                    >
                                        <Whatsapp style={{ fontSize: 20, color: '#16a34a' }} />
                                    </IconButton>
                                    <IconButton
                                        type="button"
                                        className={classes.iconRoundButton}
                                        aria-label={t('ClientDetail.details.edit')}
                                        onClick={() => setProfileModalOpen(true)}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.labelEmail')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {clientData.email || '—'}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        type="button"
                                        className={classes.iconRoundButton}
                                        aria-label={t('PreviewLink.copy')}
                                        onClick={() => handleCopy(clientData.email || '')}
                                        disabled={!clientData.email}
                                    >
                                        <FileCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    {clientData.email ? (
                                        <IconButton
                                            component="a"
                                            href={`mailto:${clientData.email}`}
                                            className={classes.iconRoundButton}
                                            aria-label={t('ClientDetail.mobile.sendEmail')}
                                        >
                                            <MailOutlineIcon fontSize="small" style={{ color: '#2563eb' }} />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            type="button"
                                            className={classes.iconRoundButton}
                                            aria-label={t('ClientDetail.mobile.sendEmail')}
                                            disabled
                                        >
                                            <MailOutlineIcon fontSize="small" style={{ color: '#2563eb' }} />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        type="button"
                                        className={classes.iconRoundButton}
                                        aria-label={t('ClientDetail.details.edit')}
                                        onClick={() => setProfileModalOpen(true)}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.labelAddress')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {addressLine || '—'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    type="button"
                                    className={classes.iconRoundButton}
                                    aria-label={t('ClientDetail.details.edit')}
                                    onClick={() => setProfileModalOpen(true)}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.labelDocument')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {[documentTypeLabel, clientData.dni].filter(Boolean).join(' ') || '—'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    type="button"
                                    className={classes.iconRoundButton}
                                    aria-label={t('ClientDetail.details.edit')}
                                    onClick={() => setProfileModalOpen(true)}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    <Box component="section" id="private_note" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h3" className={classes.sectionTitle}>
                                {t('ClientDetail.mobile.privateNoteTitle')}
                            </Typography>
                            <Typography className={classes.notesHint}>
                                {t('ClientDetail.notes.notice')}
                            </Typography>
                            <textarea
                                readOnly
                                className={classes.notesTextarea}
                                rows={3}
                                maxLength={500}
                                value={clientData.notes || ''}
                                placeholder={t('ClientDetail.mobile.notePlaceholder')}
                                aria-label={t('ClientDetail.mobile.privateNoteTitle')}
                            />
                            <Button
                                type="button"
                                variant="outlined"
                                className={classes.saveNoteButton}
                                onClick={() => setNotesModalOpen(true)}
                            >
                                {t('ClientDetail.mobile.editOrSaveNote')}
                            </Button>
                        </Box>
                    </Box>

                    <Box component="section" id="extra" className={classes.section}>
                        <Box className={classes.card}>
                            <Typography component="h3" className={classes.sectionTitle}>
                                {t('ClientDetail.mobile.extraTitle')}
                            </Typography>
                            {clientData.created_at && (
                                <Box className={classes.infoRow}>
                                    <Box className={classes.infoTextBlock}>
                                        <Typography className={classes.infoLabel}>
                                            {t('ClientDetail.mobile.registerDate')}
                                        </Typography>
                                        <Typography className={classes.infoValue}>
                                            {formatDate(clientData.created_at)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.lastContact')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {lastOrderDate ? formatDate(lastOrderDate) : '—'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={classes.infoRow}>
                                <Box className={classes.infoTextBlock}>
                                    <Typography className={classes.infoLabel}>
                                        {t('ClientDetail.mobile.preferredPayment')}
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {t('ClientDetail.mobile.notAvailable')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <EditClientDataModal
                        dataToEdit={{
                            clientId: clientData.id,
                            first_name: clientData.first_name,
                            last_name: clientData.last_name,
                            phone_number: clientData.phone_number,
                            email: clientData.email,
                            dni: clientData.dni,
                            document_type: clientData.document_type,
                            address: clientData.address,
                            address_detail: clientData.address_line,
                            city_id: clientData.city_id,
                        }}
                        open={profileModalOpen}
                        isNoClose={false}
                        handleClose={() => setProfileModalOpen(false)}
                        handleSuccess={handleProfileSaved}
                        type={1}
                    />
                    <EditClientDataModal
                        dataToEdit={{ clientId: clientData.id, notes: clientData.notes }}
                        open={notesModalOpen}
                        isNoClose={false}
                        handleClose={() => setNotesModalOpen(false)}
                        handleSuccess={handleNotesSaved}
                        type={2}
                    />
                </>
            )}
        </Box>
    );
};

const mapStateToProps = ({ auth, clients, common }) => ({
    storeId: auth.storeId,
    clientToEdit: clients.clientToEdit,
    storeData: common.storeData,
});

export default connect(mapStateToProps)(ClientInfoPageMobile);
